export interface ProviderInfo {
  provider: string;
  model?: string;
  voice?: string;
  name?: string;
  updatedAt?: string;
}

export interface JobProviders {
  script?: ProviderInfo;
  image?: ProviderInfo;
  audio?: ProviderInfo;
}

export interface Job {
  id: string;
  title: string;
  commandRevision: number;
  stateVersion: number;
  currentInputRevisionId?: string;
  currentScriptRevisionId?: string;
  currentContentRevisionId?: string;
  createdAt: string;
  updatedAt: string;
  providers?: JobProviders;
}

export interface JobSpec {
  id: string;
  projectId: string;
  schemaVersion: number;
  pipelineMode: 'scene_composition' | 'provider_full_video';
  spec: {
    preset?: string;
    flow?: 'content' | 'commerce';
    pipelineMode?: string;
    aspectRatio?: string;
    targetDurationSec?: number;
    style?: string;
    stylePrompt?: string;
    contentFlow?: string;
    contentPrompt?: string;
    providers?: JobProviders;
    [key: string]: any;
  };
}

export interface ProjectBrief {
  id: string;
  projectId: string;
  revision: number;
  flow: 'content' | 'commerce';
  topic: string;
  audience: string;
  objective: string;
  language: string;
  cta: string;
  productName?: string;
  productUrl?: string;
}

export interface Asset {
  id: string;
  projectId?: string;
  objectKey: string;
  mimeType: string;
  bytes: number;
  validationStatus: string;
}

export interface Scene {
  id: string;
  projectId: string;
  sceneKey: string;
  orderIndex: number;
  currentRevisionId?: string;
}

export interface SceneRevision {
  id: string;
  sceneId: string;
  revision: number;
  narration: string;
  captionText: string;
  visualPrompt?: string;
  timingDurationMs: number;
  motionPreset?: string;
  imageUrl?: string;
  audioUrl?: string;
  visualType?: 'image' | 'video';
  videoUrl?: string;
  referenceAssetId?: string;
  referenceImageUrl?: string;
  motionPrompt?: string;
  isApproved?: boolean;
}

export interface ProviderConnection {
  id: string;
  alias: string;
  adapterCode: string;
  enabled: boolean;
  lastHealthStatus: string;
  maskedIdentity?: string;
  baseUrl?: string;
}

export interface GenerationContextSnapshot {
  snapshotId: string;
  projectId: string;
  contextHash: string;
  compilerVersion: string;
  modality: 'text' | 'image' | 'video' | 'audio' | 'composition';
  taskType: string;
  content: {
    flow: string;
    contentPrompt: string;
    topic: string;
    audience: string;
    objective: string;
    language: string;
    cta: string;
    productName?: string;
    productUrl?: string;
    verifiedClaims?: string[];
  };
  style: {
    presetId: string;
    presetVersion: number;
    stylePrompt: string;
    negativePrompt?: string;
    aspectRatio: string;
    targetResolution?: string;
  };
  story: {
    scriptRevisionId?: string;
    scriptRevision: number;
    title: string;
    totalScenes: number;
    targetDurationSec: number;
  };
  scene?: {
    sceneId: string;
    sceneRevisionId: string;
    sceneKey: string;
    orderIndex: number;
    visualPrompt: string;
    narration: string;
    captionText: string;
    timingDurationMs: number;
    motionPreset: string;
  };
  continuity: {
    referenceAssetIds?: string[];
    assetObjectKeys?: string[];
    productIdentity?: string;
  };
  audioDirection?: {
    provider: string;
    voice: string;
    language?: string;
    rate?: string;
    pitch?: string;
    volume?: string;
    emotion?: string;
    pace?: string;
  };
  createdAt: string;
}

export interface GenerationCommand {
  schemaVersion: number;
  jobId: string;
  sceneId?: string;
  expectedSceneRevisionId?: string;
  modality: 'text' | 'image' | 'video' | 'audio' | 'composition';
  taskType: string;
  provider?: string;
  model?: string;
  voice?: string;
  idempotencyKey?: string;
}

export function createApiBase(value?: string): string {
  const apiBase = value?.trim().replace(/\/+$/, '');
  if (!apiBase) {
    throw new Error('VITE_API_URL is required in environment configuration (.env)');
  }
  return apiBase.replace(/\/api$/, '');
}

export const BASE_URL = createApiBase(import.meta.env.VITE_API_URL);

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      ...init?.headers,
    },
  });
  if (!res.ok) {
    let errMessage = `HTTP ${res.status}`;
    try {
      const cloned = res.clone();
      const body = await cloned.json();
      if (body?.error?.message) {
        errMessage = body.error.message;
      } else if (body?.message) {
        errMessage = body.message;
      } else if (body?.error) {
        errMessage = typeof body.error === 'string' ? body.error : JSON.stringify(body.error);
      }
    } catch {
      try {
        const text = await res.clone().text();
        if (text) errMessage = text;
      } catch {
        // ignore
      }
    }
    throw new Error(errMessage);
  }
  return res;
}

function getHeaders(idempotencyKey?: string): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }
  return headers;
}

export async function fetchHealth() {
  const res = await apiFetch('/health');
  return res.json();
}

export async function listJobs(): Promise<{ jobs: Job[] }> {
  const res = await apiFetch('/api/jobs');
  return res.json();
}

export async function getJob(id: string): Promise<{
  job: Job;
  project?: Job;
  spec: JobSpec;
  brief: ProjectBrief;
  providers?: JobProviders;
  latestVideoUrl?: string;
  previewVideoUrl?: string;
  contentSnapshotId?: string | null;
  previewOutputId?: string;
  scriptApproved?: boolean;
  approvedSceneRevisionIds?: string[];
  outputs?: any[];
}> {
  const res = await apiFetch(`/api/jobs/${id}`);
  return res.json();
}

export async function updateJobProviders(
  jobId: string,
  providers: JobProviders
): Promise<{ status: string; message?: string }> {
  const res = await apiFetch(`/api/jobs/${jobId}/providers`, {
    method: 'PATCH',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(providers),
  });
  return res.json();
}

export async function deleteJob(id: string): Promise<{ status: string; jobId?: string; message?: string }> {
  const res = await apiFetch(`/api/jobs/${id}`, {
    method: 'DELETE',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

export async function generateScript(jobId: string, model?: string, provider?: string): Promise<{
  status: string;
  scenesCount?: number;
  message?: string;
  error?: string;
  scenes?: Scene[];
  revisions?: SceneRevision[];
}> {
  const params = new URLSearchParams();
  if (model) params.set('model', model);
  if (provider) params.set('provider', provider);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiFetch(`/api/jobs/${jobId}/generate-script${qs}`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

export async function generateSceneImage(jobId: string, sceneId: string, model?: string, provider?: string, mode: 't2i' | 'i2i' = 't2i'): Promise<{
  status: string;
  revision?: SceneRevision;
  imageUrl?: string;
  message?: string;
  error?: string;
}> {
  const params = new URLSearchParams();
  if (model) params.set('model', model);
  if (provider) params.set('provider', provider);
  params.set('mode', mode);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiFetch(`/api/jobs/${jobId}/scenes/${sceneId}/generate-image${qs}`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

export async function generateAllImages(jobId: string, model?: string, provider?: string): Promise<{
  status: string;
  revisions?: SceneRevision[];
  count?: number;
  message?: string;
  error?: string;
}> {
  const params = new URLSearchParams();
  if (model) params.set('model', model);
  if (provider) params.set('provider', provider);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiFetch(`/api/jobs/${jobId}/generate-images${qs}`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

export async function generateSceneAudio(jobId: string, sceneId: string, voice?: string, provider?: string): Promise<{
  status: string;
  revision?: SceneRevision;
  audioUrl?: string;
  message?: string;
  error?: string;
}> {
  const params = new URLSearchParams();
  if (voice) params.set('voice', voice);
  if (provider) params.set('provider', provider);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiFetch(`/api/jobs/${jobId}/scenes/${sceneId}/generate-audio${qs}`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

export async function generateAllAudios(jobId: string, voice?: string, provider?: string): Promise<{
  status: string;
  count?: number;
  message?: string;
  error?: string;
}> {
  const params = new URLSearchParams();
  if (voice) params.set('voice', voice);
  if (provider) params.set('provider', provider);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiFetch(`/api/jobs/${jobId}/generate-audios${qs}`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

export async function getJobContext(jobId: string, sceneId?: string, modality = 'image'): Promise<{
  snapshot: GenerationContextSnapshot;
  contextHash: string;
  compiledPrompt: string;
}> {
  const qs = sceneId ? `?sceneId=${sceneId}&modality=${modality}` : `?modality=${modality}`;
  const res = await apiFetch(`/api/jobs/${jobId}/context${qs}`);
  if (!res.ok) throw new Error(`Failed to fetch job context: HTTP ${res.status}`);
  return res.json();
}

export async function dispatchModalityCommand(jobId: string, cmd: GenerationCommand): Promise<any> {
  const res = await apiFetch(`/api/jobs/${jobId}/modalities/execute`, {
    method: 'POST',
    headers: getHeaders(cmd.idempotencyKey || crypto.randomUUID()),
    body: JSON.stringify(cmd),
  });
  return res.json();
}

export async function submitScriptDecision(jobId: string, payload: {
  scriptRevisionId: string;
  decision: 'approve' | 'reject';
  feedback?: string;
}): Promise<{
  decisionId: string;
  decision: string;
  status: string;
}> {
  const res = await apiFetch(`/api/jobs/${jobId}/script-decisions`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function submitSceneDecision(jobId: string, payload: {
  contentSnapshotId: string;
  reviewedPreviewOutputId: string;
  sceneRevisionIds: string[];
  decision: 'approve' | 'reject';
  feedback?: string;
}): Promise<{
  decisionIds: string[];
  decision: string;
  status: string;
}> {
  const res = await apiFetch(`/api/jobs/${jobId}/scenes/decisions`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function createJob(payload: {
  title: string;
  pipelineMode: string;
  flow: string;
  topic: string;
  audience: string;
  objective: string;
  language: string;
  cta: string;
  preset?: string;
  aspectRatio?: string;
  targetDurationSec?: number;
  style?: string;
  stylePrompt?: string;
  defaultVisualMode?: 'image' | 'video';
  contentFlow?: string;
  contentPrompt?: string;
  scriptModel?: string;
  scriptProvider?: string;
  imageProvider?: string;
  imageModel?: string;
  audioProvider?: string;
  audioVoice?: string;
  productName?: string;
  productUrl?: string;
  assetIds?: string[];
}): Promise<{
  jobId: string;
  status: string;
  commandRevision: number;
  stateVersion: number;
  scriptStatus?: string;
  scriptMessage?: string;
  scriptError?: string;
  scenesCount?: number;
}> {
  const res = await apiFetch('/api/jobs', {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function createUploadSession(payload: {
  declaredMimeType: string;
  declaredBytes: number;
  filename: string;
  projectId?: string;
}) {
  const res = await apiFetch('/api/upload-sessions', {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function completeUploadSession(sessionId: string) {
  const res = await apiFetch(`/api/upload-sessions/${sessionId}/complete`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

/** All image attachment targets share staging, transport and final validation. */
export async function uploadAsset(file: File, projectId?: string): Promise<{ assetId: string; objectKey: string; publicUrl?: string; validationStatus: string }> {
  const contentType = file.type || 'image/jpeg';
  const session = await createUploadSession({
    declaredMimeType: contentType,
    declaredBytes: file.size,
    filename: file.name,
    ...(projectId ? { projectId } : {}),
  });
  if (!session?.uploadSessionId || (!session.uploadUrl && !session.putUrl)) {
    throw new Error('ไม่สามารถสร้าง Upload Session ได้');
  }
  if (session.uploadUrl) {
    const expectedPath = `/api/upload-sessions/${encodeURIComponent(session.uploadSessionId)}/content`;
    if (session.uploadUrl !== expectedPath) throw new Error('เส้นทางอัปโหลดไม่ตรงกับ Upload Session');
    await apiFetch(expectedPath, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
  } else {
    // Legacy backends may still expose only a presigned storage URL.
    let response: Response;
    try {
      response = await fetch(session.putUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
    } catch {
      throw new Error('เชื่อมต่อสตอเรจเพื่ออัปโหลดไม่ได้ ตรวจสอบ CORS/เครือข่าย หรืออัปเดต Backend เพื่อใช้การอัปโหลดผ่าน API');
    }
    if (!response.ok) throw new Error(`ส่งไฟล์ไปยังสตอเรจไม่สำเร็จ (HTTP ${response.status})`);
  }
  const result = await completeUploadSession(session.uploadSessionId);
  if (!result?.assetId || !result.objectKey || result.validationStatus !== 'ready') {
    throw new Error('การตรวจสอบความถูกต้องของไฟล์ (Validation) ไม่ผ่าน');
  }
  return result;
}

export async function listAssets(projectId: string): Promise<{ assets: Asset[] }> {
  const res = await apiFetch(`/api/jobs/${projectId}/assets`);
  return res.json();
}

export async function decideScript(jobId: string, scriptRevisionId: string, decision: 'approve' | 'reject', feedback?: string) {
  const res = await apiFetch(`/api/jobs/${jobId}/script-decisions`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify({ scriptRevisionId, decision, feedback }),
  });
  return res.json();
}

export async function listScenes(jobId: string): Promise<{ scenes: Scene[]; revisions: SceneRevision[] }> {
  const res = await apiFetch(`/api/jobs/${jobId}/scenes`);
  return res.json();
}

export async function patchScene(jobId: string, sceneId: string, patch: Partial<SceneRevision>) {
  const res = await apiFetch(`/api/jobs/${jobId}/scenes/${sceneId}`, {
    method: 'PATCH',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(patch),
  });
  return res.json();
}

export async function toggleSceneApproval(jobId: string, sceneId: string): Promise<{
  status: string;
  sceneId: string;
  revisionId: string;
  isApproved: boolean;
}> {
  const res = await apiFetch(`/api/jobs/${jobId}/scenes/${sceneId}/approve`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

export async function approveAllScenes(jobId: string): Promise<{
  status: string;
  message: string;
}> {
  const res = await apiFetch(`/api/jobs/${jobId}/approve-all`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

export async function decideScenes(jobId: string, payload: {
  contentSnapshotId: string;
  reviewedPreviewOutputId: string;
  sceneRevisionIds: string[];
  decision: 'approve' | 'reject';
}) {
  const res = await apiFetch(`/api/jobs/${jobId}/scene-decisions`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function dispatchRender(jobId: string, purpose: 'preview' | 'final', contentSnapshotId: string, renderSettings: any): Promise<{
  status: string;
  renderJobId?: string;
  contentSnapshotId?: string;
  operationId?: string;
  purpose?: string;
  message?: string;
  error?: { code: string; message: string };
}> {
  const res = await apiFetch(`/api/jobs/${jobId}/renders`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify({ purpose, contentSnapshotId, renderSettings }),
  });
  return res.json();
}

export async function pollRender(renderJobId: string): Promise<{
  status: string;
  renderJobId: string;
  progressPercent: number;
  progressPhase: string;
  videoUrl?: string;
  outputId?: string;
  renderJob?: any;
}> {
  const res = await apiFetch(`/api/renders/${renderJobId}`);
  return res.json();
}

export async function listProviderConnections(): Promise<{ connections: ProviderConnection[] }> {
  const res = await apiFetch('/api/provider-connections');
  return res.json();
}

export async function createProviderConnection(payload: {
  alias: string;
  adapterCode: string;
  baseUrl: string;
  secretKey: string;
  config?: any;
}) {
  const res = await apiFetch('/api/provider-connections', {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function deleteProviderConnection(id: string) {
  const res = await apiFetch(`/api/provider-connections/${id}`, {
    method: 'DELETE',
    headers: getHeaders(crypto.randomUUID()),
  });
  return res.json();
}

export async function getLocalMediaModels(): Promise<{
  status: string;
  count: number;
  models: Array<{ id: string; name: string; source: 'lmstudio' | 'ollama'; size?: number }>;
}> {
  const res = await apiFetch('/api/local-media/models');
  return res.json();
}

export async function getLocalMediaImageModels(refreshProvider?: string): Promise<{
  status: string;
  online: boolean;
  count: number;
  models: Array<{ id: string; name: string; source: string; desc?: string; type?: string }>;
}> {
  const query = refreshProvider ? `?${new URLSearchParams({ refresh_browser: 'true', provider: refreshProvider })}` : '';
  const res = await apiFetch(`/api/local-media/image-models${query}`);
  return res.json();
}

export async function getLocalMediaVideoModels(refreshProvider?: string): Promise<{
  status: string;
  online: boolean;
  count: number;
  models: Array<{ name: string; type: string; supportedModes: string[]; provider?: string }>;
}> {
  const query = refreshProvider ? `?${new URLSearchParams({ refresh_browser: 'true', provider: refreshProvider })}` : '';
  const res = await apiFetch(`/api/local-media/video-models${query}`);
  return res.json();
}

export async function getLocalMediaAudioVoices(): Promise<{
  status: string;
  providers: Record<string, Array<{ id: string; name: string; gender?: string }>>;
}> {
  const res = await apiFetch('/api/local-media/audio-voices');
  return res.json();
}

export async function suggestMotionPrompt(
  jobId: string,
  sceneId: string
): Promise<{ status: string; motionPrompt: string }> {
  const res = await apiFetch(`/api/jobs/${jobId}/scenes/${sceneId}/suggest-motion-prompt`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify({}),
  });
  return res.json();
}

export async function setSceneReferenceImage(
  jobId: string,
  sceneId: string,
  payload: { referenceAssetId?: string; sourceSceneId?: string; referenceImageUrl?: string; visualType?: 'image' | 'video' }
): Promise<{ status: string; sceneRevisionId: string; referenceAssetId?: string; referenceImageUrl?: string }> {
  const res = await apiFetch(`/api/jobs/${jobId}/scenes/${sceneId}/reference-image`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function setSceneVisualImage(
  jobId: string,
  sceneId: string,
  payload: { action: 'clear' } | { action: 'attach'; assetId: string }
): Promise<{ status: string; sceneRevisionId: string; imageUrl: string | null; referenceImageUrl: null }> {
  const res = await apiFetch(`/api/jobs/${jobId}/scenes/${sceneId}/visual-image`, {
    method: 'PUT',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function generateSceneVideo(
  jobId: string,
  sceneId: string,
  payload: { mode: 'i2v' | 't2v'; imageSource?: 'generated' | 'reference'; motionPrompt?: string; provider?: string; model?: string }
): Promise<{ status: string; revision: SceneRevision; videoUrl?: string; motionPrompt?: string }> {
  const res = await apiFetch(`/api/jobs/${jobId}/scenes/${sceneId}/generate-video`, {
    method: 'POST',
    headers: getHeaders(crypto.randomUUID()),
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (data.status === 'failed' || data.error) {
    throw new Error(data.message || data.error?.message || 'Failed to generate scene video');
  }
  return data;
}


